import {
  CSSProperties,
  ReactNode,
  createElement,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isMobileApp } from "@common/core/platform-detection";
import { useIsMobileMediaQuery } from "@ui/utils/hooks/is-mobile-media-query";
import { useCuedTrack } from "@app/web-player/player-controls/use-cued-track";
import { getTrackImageSrc } from "@app/web-player/tracks/track-image/track-image";

interface RgbColor {
  r: number;
  g: number;
  b: number;
}

interface HslColor {
  h: number;
  s: number;
  l: number;
}

interface AmbientBucket {
  totalWeight: number;
  r: number;
  g: number;
  b: number;
}

interface AmbientPalette {
  primary: RgbColor;
  secondary: RgbColor;
  accent: RgbColor;
}

export type PlayerAmbientStyle = CSSProperties & {
  "--player-ambient-primary"?: string;
  "--player-ambient-secondary"?: string;
  "--player-ambient-accent"?: string;
};

const SAMPLE_SIZE = 24;
const DEFAULT_PALETTE: AmbientPalette = {
  primary: { r: 120, g: 142, b: 182 },
  secondary: { r: 61, g: 83, b: 118 },
  accent: { r: 201, g: 129, b: 95 },
};
const paletteCache = new Map<string, AmbientPalette>();
const APP_AMBIENT_PROPERTIES = [
  "--player-ambient-primary",
  "--player-ambient-secondary",
  "--player-ambient-accent",
] as const;
let lastAppliedAppAmbientStyle: PlayerAmbientStyle | undefined;
const PlayerAmbientStyleContext = createContext<{
  isActive: boolean;
  style?: PlayerAmbientStyle;
}>({
  isActive: false,
});

export function getLastPlayerAmbientStyle() {
  return lastAppliedAppAmbientStyle;
}

export function PlayerAmbientStyleProvider({
  children,
}: {
  children: ReactNode;
}) {
  const value = useResolvedPlayerAmbientStyle();
  return createElement(PlayerAmbientStyleContext.Provider, { value }, children);
}

export function usePlayerAmbientStyle() {
  return useContext(PlayerAmbientStyleContext);
}

function useResolvedPlayerAmbientStyle(): {
  isActive: boolean;
  style?: PlayerAmbientStyle;
} {
  const isNativeMobileApp = isMobileApp();
  const isMobileViewport = !!useIsMobileMediaQuery();
  const ambientEnabled = isNativeMobileApp || isMobileViewport;
  const track = useCuedTrack();
  const artwork =
    ambientEnabled && track ? getTrackImageSrc(track, "square") : undefined;
  const [palette, setPalette] = useState<AmbientPalette | null>(null);

  useEffect(() => {
    if (!artwork) {
      setPalette(null);
      return;
    }

    const cachedPalette = paletteCache.get(artwork);
    if (cachedPalette) {
      setPalette(cachedPalette);
      return;
    }

    let cancelled = false;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";
    image.onload = () => {
      if (cancelled) return;

      const nextPalette = safelyExtractAmbientPalette(image);
      paletteCache.set(artwork, nextPalette);
      setPalette(nextPalette);
    };
    image.onerror = () => {
      if (cancelled) return;

      paletteCache.set(artwork, DEFAULT_PALETTE);
      setPalette(DEFAULT_PALETTE);
    };
    image.src = artwork;

    return () => {
      cancelled = true;
      image.onload = null;
      image.onerror = null;
    };
  }, [artwork]);

  const style = useMemo<PlayerAmbientStyle | undefined>(() => {
    if (!artwork) {
      return undefined;
    }

    const resolvedPalette =
      paletteCache.get(artwork) ?? palette ?? DEFAULT_PALETTE;

    return {
      "--player-ambient-primary": toRgba(resolvedPalette.primary, 0.42),
      "--player-ambient-secondary": toRgba(resolvedPalette.secondary, 0.3),
      "--player-ambient-accent": toRgba(resolvedPalette.accent, 0.26),
    };
  }, [artwork, palette]);

  useEffect(() => {
    if (!isNativeMobileApp) {
      return;
    }

    const body = document.body;
    if (!body) {
      return;
    }

    body.classList.add("player-ambient-app-active");

    if (style && artwork) {
      lastAppliedAppAmbientStyle = style;
      APP_AMBIENT_PROPERTIES.forEach((propertyName) => {
        const value = style[propertyName];
        if (value) {
          body.style.setProperty(propertyName, value);
        }
      });
    } else {
      lastAppliedAppAmbientStyle = undefined;
      APP_AMBIENT_PROPERTIES.forEach((propertyName) => {
        body.style.removeProperty(propertyName);
      });
    }
  }, [artwork, isNativeMobileApp, style]);

  useEffect(() => {
    if (!isNativeMobileApp) {
      return;
    }

    const body = document.body;
    if (!body) {
      return;
    }

    return () => {
      body.classList.remove("player-ambient-app-active");
      APP_AMBIENT_PROPERTIES.forEach((propertyName) => {
        body.style.removeProperty(propertyName);
      });
    };
  }, [isNativeMobileApp]);

  return {
    isActive: ambientEnabled && Boolean(artwork),
    style,
  };
}

function safelyExtractAmbientPalette(image: HTMLImageElement): AmbientPalette {
  try {
    return extractAmbientPalette(image);
  } catch {
    return DEFAULT_PALETTE;
  }
}

function extractAmbientPalette(image: HTMLImageElement): AmbientPalette {
  const canvas = document.createElement("canvas");
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;

  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    return DEFAULT_PALETTE;
  }

  context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

  const { data } = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
  const buckets = new Map<string, AmbientBucket>();
  const fallbackBucket: AmbientBucket = {
    totalWeight: 0,
    r: 0,
    g: 0,
    b: 0,
  };

  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3] / 255;
    if (alpha < 0.45) {
      continue;
    }

    const color: RgbColor = {
      r: data[index],
      g: data[index + 1],
      b: data[index + 2],
    };
    const { s, l } = rgbToHsl(color);
    const weight =
      alpha * (0.35 + s * 1.4) * Math.max(0.24, 1 - Math.abs(l - 0.52) * 1.7);

    fallbackBucket.totalWeight += alpha;
    fallbackBucket.r += color.r * alpha;
    fallbackBucket.g += color.g * alpha;
    fallbackBucket.b += color.b * alpha;

    if (weight <= 0.01) {
      continue;
    }

    const quantized = quantizeColor(color);
    const key = `${quantized.r}-${quantized.g}-${quantized.b}`;
    const bucket = buckets.get(key) ?? {
      totalWeight: 0,
      r: 0,
      g: 0,
      b: 0,
    };

    bucket.totalWeight += weight;
    bucket.r += color.r * weight;
    bucket.g += color.g * weight;
    bucket.b += color.b * weight;

    buckets.set(key, bucket);
  }

  const candidates = Array.from(buckets.values())
    .sort((left, right) => right.totalWeight - left.totalWeight)
    .slice(0, 6)
    .map(getBucketAverageColor);
  const seed =
    candidates[0] ??
    (fallbackBucket.totalWeight
      ? getBucketAverageColor(fallbackBucket)
      : DEFAULT_PALETTE.primary);
  const accentCandidate =
    getMostSaturatedColor(candidates) ??
    shiftColor(seed, {
      hueShift: 18,
      saturationMultiplier: 1.2,
      lightnessDelta: 0.08,
    });
  const distantCandidate = getMostDistantColor(candidates, seed);
  const secondaryCandidate =
    distantCandidate && colorDistance(seed, distantCandidate) >= 54
      ? distantCandidate
      : shiftColor(seed, {
          hueShift: -28,
          saturationMultiplier: 1.05,
          lightnessDelta: -0.1,
        });

  return {
    primary: normalizeColor(seed, {
      minSaturation: 0.24,
      maxSaturation: 0.76,
      minLightness: 0.34,
      maxLightness: 0.64,
    }),
    secondary: normalizeColor(secondaryCandidate, {
      minSaturation: 0.2,
      maxSaturation: 0.72,
      minLightness: 0.24,
      maxLightness: 0.56,
    }),
    accent: normalizeColor(accentCandidate, {
      minSaturation: 0.36,
      maxSaturation: 0.9,
      minLightness: 0.42,
      maxLightness: 0.72,
    }),
  };
}
function quantizeColor(color: RgbColor): RgbColor {
  return {
    r: Math.round(color.r / 24) * 24,
    g: Math.round(color.g / 24) * 24,
    b: Math.round(color.b / 24) * 24,
  };
}

function getBucketAverageColor(bucket: AmbientBucket): RgbColor {
  const weight = Math.max(bucket.totalWeight, 1);

  return {
    r: Math.round(bucket.r / weight),
    g: Math.round(bucket.g / weight),
    b: Math.round(bucket.b / weight),
  };
}

function getMostSaturatedColor(colors: RgbColor[]): RgbColor | undefined {
  return colors.reduce<RgbColor | undefined>((selected, color) => {
    if (!selected) {
      return color;
    }

    return rgbToHsl(color).s > rgbToHsl(selected).s ? color : selected;
  }, undefined);
}

function getMostDistantColor(
  colors: RgbColor[],
  target: RgbColor,
): RgbColor | undefined {
  return colors.reduce<RgbColor | undefined>((selected, color) => {
    if (colorDistance(color, target) < 40) {
      return selected;
    }

    if (!selected) {
      return color;
    }

    return colorDistance(color, target) > colorDistance(selected, target)
      ? color
      : selected;
  }, undefined);
}

function shiftColor(
  color: RgbColor,
  options: {
    hueShift?: number;
    saturationMultiplier?: number;
    lightnessDelta?: number;
  },
): RgbColor {
  const { h, s, l } = rgbToHsl(color);

  return hslToRgb({
    h: (h + (options.hueShift ?? 0) + 360) % 360,
    s: clamp(s * (options.saturationMultiplier ?? 1), 0, 1),
    l: clamp(l + (options.lightnessDelta ?? 0), 0, 1),
  });
}

function normalizeColor(
  color: RgbColor,
  options: {
    minSaturation: number;
    maxSaturation: number;
    minLightness: number;
    maxLightness: number;
  },
): RgbColor {
  const { h, s, l } = rgbToHsl(color);

  return hslToRgb({
    h,
    s: clamp(s, options.minSaturation, options.maxSaturation),
    l: clamp(l, options.minLightness, options.maxLightness),
  });
}

function rgbToHsl(color: RgbColor): HslColor {
  const r = color.r / 255;
  const g = color.g / 255;
  const b = color.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;

  if (delta === 0) {
    return { h: 0, s: 0, l: lightness };
  }

  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue = 0;

  switch (max) {
    case r:
      hue = (g - b) / delta + (g < b ? 6 : 0);
      break;
    case g:
      hue = (b - r) / delta + 2;
      break;
    default:
      hue = (r - g) / delta + 4;
      break;
  }

  return {
    h: hue * 60,
    s: saturation,
    l: lightness,
  };
}

function hslToRgb(color: HslColor): RgbColor {
  if (color.s === 0) {
    const channel = Math.round(color.l * 255);
    return { r: channel, g: channel, b: channel };
  }

  const hue = color.h / 360;
  const q =
    color.l < 0.5
      ? color.l * (1 + color.s)
      : color.l + color.s - color.l * color.s;
  const p = 2 * color.l - q;

  return {
    r: Math.round(hueToRgb(p, q, hue + 1 / 3) * 255),
    g: Math.round(hueToRgb(p, q, hue) * 255),
    b: Math.round(hueToRgb(p, q, hue - 1 / 3) * 255),
  };
}

function hueToRgb(p: number, q: number, t: number): number {
  let normalized = t;

  if (normalized < 0) {
    normalized += 1;
  }
  if (normalized > 1) {
    normalized -= 1;
  }
  if (normalized < 1 / 6) {
    return p + (q - p) * 6 * normalized;
  }
  if (normalized < 1 / 2) {
    return q;
  }
  if (normalized < 2 / 3) {
    return p + (q - p) * (2 / 3 - normalized) * 6;
  }

  return p;
}

function colorDistance(left: RgbColor, right: RgbColor): number {
  const deltaR = left.r - right.r;
  const deltaG = left.g - right.g;
  const deltaB = left.b - right.b;

  return Math.sqrt(deltaR ** 2 + deltaG ** 2 + deltaB ** 2);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function toRgba(color: RgbColor, alpha: number): string {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}
